import { useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors, DeleteFilledIcon } from "@dotnet/telematics-design-system";

import DateTime from "components/DateTime";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral,
    TooltipLarge
} from "components/DesignComponents/Tooltips";
import {
    body1SemiBoldStyles,
    body2SemiBoldStyles,
    COMPANY_LEVEL_SETTINGS,
    ENDPOINTS,
    FEATURE_SETTINGS,
    svgFontSize,
    useApi,
    useCompanyLevelSettings,
    usePermissions
} from "shared";

import { TOKEN_TYPES, TokenProps } from "../../types";
import StatusIcon from "./StatusIcon";

const Token = ({ data, isFirst, isLast, removeFromList }: TokenProps) => {
    const { id, name, type, created_at, expires_at, is_expired, last_used_at } =
        data;

    const { gray200, gray700, gray900, red800, textDisabled } = colors;

    const { t } = useTranslation();
    const { deleteData, handleResponse } = useApi();
    const { isFeatureSettingsRestricted } = useCompanyLevelSettings();

    const {
        isDeleteTokenNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const [isLoading, setIsLoading] = useState(false);

    const deleteToken = async () => {
        try {
            setIsLoading(true);

            const response = await deleteData(ENDPOINTS.Tokens, id);

            removeFromList(id);
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    };

    const getExpiryDateStyles = () => {
        if (isRestricted) {
            return {
                color: textDisabled,
                fontWeight: "500"
            };
        }

        return is_expired
            ? {
                  color: red800,
                  fontWeight: "600"
              }
            : { color: gray900, fontWeight: "500" };
    };

    const isRestricted =
        type === TOKEN_TYPES.Integration &&
        isFeatureSettingsRestricted(FEATURE_SETTINGS.ApiTokenSupport);

    const tooltipTitle =
        getNoPermissionTooltipTitle(PERMISSIONS.TokenDelete) ||
        t("Button##delete");

    return (
        <div
            css={css({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                wordBreak: "break-word",
                borderBottom: isLast ? "none" : `1px solid ${gray200}`,
                paddingTop: isFirst ? "0" : "16px",
                paddingBottom: isLast ? "0" : "16px"
            })}
        >
            <TooltipLarge
                title={
                    isRestricted
                        ? t(
                              `Restrictions##${COMPANY_LEVEL_SETTINGS.FeatureSettings}##${FEATURE_SETTINGS.ApiTokenSupport}##token list`
                          )
                        : ""
                }
                placement={TOOLTIP_PLACEMENT.Top}
            >
                <div
                    css={css({
                        "& > div:first-of-type": {
                            ...body1SemiBoldStyles,
                            display: "flex",
                            alignItems: "center",
                            color: isRestricted ? textDisabled : gray900,

                            "& > span:first-of-type": {
                                display: "flex",
                                marginRight: "8px",

                                "& > svg": {
                                    ...svgFontSize(16)
                                }
                            }
                        },

                        "& > div:not(:first-of-type)": {
                            ...body2SemiBoldStyles,

                            "& > span:first-of-type": {
                                color: isRestricted ? textDisabled : gray700,
                                marginRight: "5px"
                            },

                            "& > span:last-of-type": {
                                color: isRestricted ? textDisabled : gray900
                            }
                        },

                        "& > div:nth-of-type(4)": {
                            "& > span:last-of-type": {
                                ...getExpiryDateStyles()
                            }
                        },

                        "& > div + div": {
                            marginTop: "4px"
                        }
                    })}
                >
                    <div>
                        <StatusIcon
                            isExpired={is_expired}
                            isRestricted={isRestricted}
                        />

                        <span>{name}</span>
                    </div>

                    <div>
                        <span>{t("Api tokens##token type")}:</span>
                        <span>{t(`Api tokens##titles##${type}##name`)}</span>
                    </div>

                    <div>
                        <span>{t("Table##created at")}:</span>

                        <span>
                            <DateTime date={created_at} />
                        </span>
                    </div>

                    <div>
                        <span>{t("Table##expiry date")}:</span>

                        <span
                            css={css({
                                color: red800
                            })}
                        >
                            <DateTime date={expires_at} format="L" />
                        </span>
                    </div>

                    <div>
                        <span>{t("Api tokens##last used at")}:</span>

                        <span>
                            {last_used_at ? (
                                <DateTime date={last_used_at} />
                            ) : (
                                t("Api tokens##never")
                            )}
                        </span>
                    </div>
                </div>
            </TooltipLarge>

            <TooltipGeneral
                placement={TOOLTIP_PLACEMENT.Top}
                title={tooltipTitle}
                disableTouchListener
            >
                <span>
                    <Button
                        disableRipple
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Tiny}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={<DeleteFilledIcon />}
                        isLoading={isLoading}
                        disabled={isDeleteTokenNotAllowed}
                        onClick={deleteToken}
                        css={css({
                            color: gray700
                        })}
                    />
                </span>
            </TooltipGeneral>
        </div>
    );
};

export default Token;
